/**
 * Service validation utilities
 */

export class ServiceValidator {
  private static readonly RESERVED_METHOD_NAMES = [
    "constructor",
    "prototype",
    "toString",
    "valueOf",
    "hasOwnProperty",
    "isPrototypeOf",
    "propertyIsEnumerable",
    "__proto__",
    "__defineGetter__",
    "__defineSetter__",
    "__lookupGetter__",
    "__lookupSetter__",
  ];

  private static readonly SERVICE_NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

  /**
   * Validate service implementation
   */
  static validateService(name: string, implementation: any): void {
    this.validateServiceName(name);
    this.validateServiceImplementation(implementation);
    this.validateServiceMethods(implementation);
  }

  /**
   * Validate service name format
   */
  private static validateServiceName(name: string): void {
    if (!name || name.trim() === "") {
      throw new Error("Service name cannot be empty");
    }

    if (!this.SERVICE_NAME_REGEX.test(name)) {
      if (/^[0-9]/.test(name)) {
        throw new Error("Service name must start with a letter");
      }
      throw new Error("Service name contains invalid characters");
    }
  }

  /**
   * Validate service implementation object
   */
  private static validateServiceImplementation(implementation: any): void {
    if (implementation === null || implementation === undefined) {
      throw new Error("Service implementation cannot be null or undefined");
    }

    if (typeof implementation !== "object" || Array.isArray(implementation)) {
      throw new Error("Service implementation must be an object");
    }
  }

  /**
   * Validate service methods
   */
  private static validateServiceMethods(implementation: any): void {
    const methods = this.getServiceMethods(implementation);

    if (methods.length === 0) {
      throw new Error("Service must have at least one method");
    }

    // Validate each method
    for (const methodName of methods) {
      this.validateMethodName(methodName);
      this.validateMethodSignature(implementation[methodName], methodName);
    }
  }

  /**
   * Validate method name
   */
  private static validateMethodName(methodName: string): void {
    if (this.RESERVED_METHOD_NAMES.includes(methodName)) {
      throw new Error(`Method name is reserved: ${methodName}`);
    }

    // Method names should follow similar rules as service names
    if (!this.SERVICE_NAME_REGEX.test(methodName)) {
      throw new Error(`Invalid method name: ${methodName}`);
    }
  }

  /**
   * Validate method signature
   */
  private static validateMethodSignature(
    method: Function,
    methodName: string,
  ): void {
    const paramCount = method.length;

    // Methods should accept 0-2 parameters: (payload?, context?)
    if (paramCount > 2) {
      throw new Error(
        `Method ${methodName} has too many parameters (expected 0-2, got ${paramCount})`,
      );
    }
  }

  /**
   * Get list of method names from service
   */
  static getServiceMethods(implementation: any): string[] {
    const methods: string[] = [];

    for (const prop in implementation) {
      if (typeof implementation[prop] === "function") {
        methods.push(prop);
      }
    }

    return methods.sort(); // Return sorted for consistent ordering
  }

  /**
   * Check if a method name is reserved
   */
  static isReservedMethodName(methodName: string): boolean {
    return this.RESERVED_METHOD_NAMES.includes(methodName);
  }

  /**
   * Validate service name format (public method)
   */
  static isValidServiceName(name: string): boolean {
    try {
      this.validateServiceName(name);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get validation errors without throwing
   */
  static getValidationErrors(name: string, implementation: any): string[] {
    const errors: string[] = [];

    try {
      this.validateServiceName(name);
    } catch (error) {
      errors.push((error as Error).message);
    }

    try {
      this.validateServiceImplementation(implementation);
    } catch (error) {
      errors.push((error as Error).message);
      return errors; // Can't continue without valid implementation
    }

    try {
      this.validateServiceMethods(implementation);
    } catch (error) {
      errors.push((error as Error).message);
    }

    return errors;
  }
}
